import { useState, useMemo } from "react";
import { useKBDocuments } from "../../hooks/useKnowledgeBase";
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { defineMessages, useIntl } from "react-intl";
import { gridCellExpandRenderer, useDataGridPremium } from "@myplant-io/mui";
import { DocumentDetailDialog } from "./DocumentDetailDialog";
import { Document } from "../../types";
import { IconButton, Input, InputAdornment, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import LaunchIcon from "@mui/icons-material/Launch";
import { useDebouncedPickerQuery } from "../../hooks/useDebouncedPickerQuery";
import { commonMessages } from "../../commonMessages";

declare module "@mui/x-data-grid-premium" {
  interface ToolbarPropsOverrides {
    onRestore: () => void;
  }
}
const GRID_STATE_ID = "KNOWLEDGE_BASE_GRID_STATE";

const messages = defineMessages({
  header: {
    id: "knowledgeBase.header",
    defaultMessage: "Knowledge Base",
  },
  name: {
    id: "knowledgeBase.column.name",
    defaultMessage: "Name",
  },
  documentType: {
    id: "knowledgeBase.column.documentType",
    defaultMessage: "Document Type",
  },
  link: {
    id: "knowledgeBase.column.link",
    defaultMessage: "Link",
  },
  chunks: {
    id: "knowledgeBase.column.chunks",
    defaultMessage: "Chunks",
  },
});

export default function KnowledgeBase() {
  const { formatMessage: t } = useIntl();
  const [isResized, setIsResized] = useState(
    JSON.parse(localStorage.getItem(`${GRID_STATE_ID}_premium`) ?? "{}")?.state
      ?.columns?.dimensions?.name?.width > 1
  );
  const searchQuery = useDebouncedPickerQuery();
  const { data: allDocuments, isLoading } = useKBDocuments();
  const [selectedDocument, setSelectedDocument] = useState<
    Document | undefined
  >();

  const columns: GridColDef<Document>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: t(messages.name),
        flex: isResized ? 0 : 1,
        renderCell: gridCellExpandRenderer,
      },
      {
        field: "document_type",
        headerName: t(messages.documentType),
        width: 150,
      },
      {
        field: "chunks",
        headerName: t(messages.chunks),
        width: 150,
      },
      {
        field: "link",
        headerName: t(messages.link),
        width: 150,
        type: "actions",
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            key="link"
            icon={<LaunchIcon />}
            label={t(messages.link)}
            onClick={() => {
              window.open(
                `/api/knowledge-base/documents/${params.id}/link`,
                "_blank"
              );
            }}
          />,
        ],
      },
    ],
    [isResized, t]
  );

  const apiRef = useGridApiRef();
  const { gridProps, onRestore } = useDataGridPremium({
    apiRef,
    gridStateId: "KNOWLEDGE_BASE_GRID_STATE",
    columns,
    autoPageSize: true,
    slotProps: {
      toolbar: {
        onRestore: () => {
          setIsResized(false);
          onRestore();
        },
      },
    },
    initialState: {
      sorting: {
        sortModel: [
          {
            field: "name",
            sort: "asc",
          },
        ],
      },
      columns: {
        columnVisibilityModel: {
          chunks: false,
        },
      },
    },
  });

  const documents = useMemo(() => {
    return (
      allDocuments
        ?.filter(({ name }) =>
          name.toLowerCase().includes(searchQuery.query.toLowerCase())
        )
        ?.map((document) => ({
          ...document,
          name: document.name || "N/A",
        })) ?? []
    );
  }, [allDocuments, searchQuery.query]);

  return (
    <>
      <div className="p-4 sm:px-6 py-4">
        <Typography variant="h4">{t(messages.header)}</Typography>
      </div>

      <div className="flex flex-col flex-grow p-4 sm:px-6 py-4">
        <div className="flex flex-col flex-grow">
          <div className="flex justify-end my-4">
            <Input
              data-testid="knowledge-base-search"
              id="search"
              placeholder={t(commonMessages.search)}
              value={searchQuery.inputValue}
              onChange={(e) => searchQuery.setQuery(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    disabled={searchQuery.inputValue !== "" ? false : true}
                    aria-label="clear search"
                    onClick={() => searchQuery.setQuery("")}
                    size="large"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </div>
          <DataGridPremium<Document>
            {...gridProps}
            loading={isLoading}
            rows={documents}
            pagination
            disableRowSelectionOnClick
            onRowClick={(args) => setSelectedDocument(args.row)}
            onColumnResize={(params) => {
              if (params.colDef.field === "name") {
                setIsResized(true);
              }
            }}
          />
        </div>
      </div>

      <DocumentDetailDialog
        open={!!selectedDocument}
        onClose={() => setSelectedDocument(undefined)}
        document={selectedDocument}
      />
    </>
  );
}
